'use client';

import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/navigation';
import { CloseIconRed, UserRoleIcon, EmailIcon, LogoutIcon, SettingsIcon, EditFilledIcon } from '@/components/icons';
import { useAuth } from '@/context/AuthContext';
import { i18n } from '@/config/i18n';
import { theme } from '@/config/theme';
import { routes } from '@/config/routes';
import { UserRole } from '@/types/auth';
import { Avatar, Button } from '@/components/ui-components';
import { AddEditUserModal } from '@/components/modals/AddEditUserModal/AddEditUserModal';
import { updateUser } from '@/services/userService';
import { useToast } from '@/context/ToastContext';
import { ApiError } from '@/services/apiService';
import type { User } from '@/components/forms/ConfigureForms/ManageUsersSection';

export interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-start;
  justify-content: flex-end;
  z-index: 9999;
  padding: 84px 24px 20px 20px;
  animation: ${({ $isOpen }) => ($isOpen ? 'fadeIn 0.2s ease' : 'none')};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  
  /* Blur overlay that excludes navbar (60px height) */
  &::before {
    content: '';
    position: absolute;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: -1;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 100%;
  max-width: fit-content;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease;
  
  @keyframes slideIn {
    from {
      transform: translateY(-10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    max-width: calc(100vw - 48px);
  }
`;

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  min-width: 250px;
  position: relative;
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  &:focus {
    outline: 2px solid #FB6F2A;
    outline-offset: 2px;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProfilePicture = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const UserNameWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 21px;
  width: 100%;
`;

const UserName = styled.div`
  font-size: 14px;
  line-height: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray[900]};
  text-align: center;
`;

const EditIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background-color 0.2s;
  color: ${({ theme }) => theme.colors.gray[600]};
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: ${({ theme }) => theme.colors.primary.accent};
  }
  
  &:focus {
    outline: 2px solid #FB6F2A;
    outline-offset: 2px;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const UserDetails = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 24px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DetailIcon = styled.div`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray[550]};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const DetailText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray[550]};
  flex: 1;
`;

const SettingsButton = styled(Button)`
  width: calc(100% - 48px);
  min-width: 202px;
  margin-bottom: 24px;
&:hover:not(:disabled) {
          transform: translateY(-2px);
  }
`;

const LogoutButton = styled(Button)`
  width: calc(100% - 48px);
  min-width: 202px;
 &:hover:not(:disabled) {
          transform: translateY(-2px);
  }
`;

const formatUserRole = (role: UserRole): string => {
  switch (role) {
    case UserRole.SUPER_ADMIN:
      return 'Super Admin';
    case UserRole.ADMIN:
      return 'Admin';
    case UserRole.USER:
      return 'User';
    default:
      return role;
  }
};

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, logout, updateUser: updateAuthUser } = useAuth();
  const router = useRouter();
  const { showToast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Get user name from auth context
  const userName = user 
    ? `${user.firstName || ''} ${user.lastName || ''}`?.trim() || user.username
    : i18n.navbar.defaultUserName;
  
  // Convert AuthContext User to ManageUsersSection User type for the modal
  const editingUser = useMemo(() => {
    if (!user) return null;
    
    // Get first name and last name, parsing from username if needed
    let first_name = user.firstName || '';
    let last_name = user.lastName || '';
    
    // If firstName/lastName are not available, try to parse from username
    if (!first_name && !last_name && user.username) {
      const nameParts = user.username?.trim().split(/\s+/);
      if (nameParts?.length >= 2) {
        first_name = nameParts[0];
        last_name = nameParts.slice(1).join(' ');
      } else if (nameParts?.length === 1) {
        first_name = nameParts[0];
        last_name = '';
      }
    }
    
    return {
      id: user.userId || user.email || '',
      email: user.email || '',
      first_name,
      last_name,
      role: user.role,
      dateAdded: new Date().toISOString().split('T')[0], // Placeholder, not used in edit
      status: 'active' as const, // Placeholder, not used in edit
    };
  }, [user]);
  
  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };
  
  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
  };
  
  const handleUserSave = async (userData: { email: string; first_name: string; last_name: string; role: UserRole }) => {
    if (!user) return;
    
    try {
      // Map UserRole enum to API string format
      const roleMap: Record<UserRole, string> = {
        [UserRole.USER]: 'USER',
        [UserRole.ADMIN]: 'ADMIN',
        [UserRole.SUPER_ADMIN]: 'SUPER_ADMIN',
      };

      // Build update payload
      const updatePayload: {
        email: string;
        first_name: string;
        last_name: string;
        role?: string;
      } = {
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
      };

      // Only include role in payload if logged-in user is not USER
      if (user.role !== UserRole.USER) {
        updatePayload.role = roleMap[userData.role];
      }

      // Update user via API
      await updateUser(user.userId || user.email || '', updatePayload);

      // Update auth context user data
      updateAuthUser({
        firstName: userData.first_name,
        lastName: userData.last_name,
      });

      showToast({
        message: i18n.settings.userManagement.messages.userUpdated || 'User updated successfully',
        type: 'success',
      });
      
      setIsEditModalOpen(false);
    } catch (error) {
      let errorMessage = i18n.settings.userManagement.messages.userUpdateFailed || 'Failed to update user. Please try again.';

      if (error instanceof ApiError) {
        const apiMessage = error.data && typeof error.data === 'object' && ('message' in error.data || 'detail' in error.data)
          ? String((error.data as { message?: string }).message || (error.data as { detail?: string }).detail || error.detail)
          : error.message;
        errorMessage = apiMessage || errorMessage;
      }

      showToast({
        message: errorMessage,
        type: 'error',
      });
    }
  };
  
  const handleSettings = () => {
    router.push(routes.settings.main);
    onClose();
  };
  
  const handleLogout = () => {
    logout();
    onClose();
  };
  
  
  // Close modal on Escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalContentWrapper>
          <CloseButtonWrapper>
            <CloseButton onClick={onClose} aria-label="Close">
              <CloseIconRed />
            </CloseButton>
          </CloseButtonWrapper>
          
          <ProfilePicture>
            <Avatar 
              userName={userName}
              size="lg"
            />
          </ProfilePicture>
          
          <UserNameWrapper>
            <UserName>{userName}</UserName>
            <EditIconButton onClick={handleEditClick} aria-label="Edit user">
              <EditFilledIcon/>
            </EditIconButton>
          </UserNameWrapper>
          
          <UserDetails>
            {user?.role && (
              <DetailItem>
                <DetailIcon>
                  <UserRoleIcon />
                </DetailIcon>
                <DetailText>{formatUserRole(user.role)}</DetailText>
              </DetailItem>
            )}
            
            {user?.email && (
              <DetailItem>
                <DetailIcon>
                  <EmailIcon />
                </DetailIcon>
                <DetailText>{user.email}</DetailText>
              </DetailItem>
            )}
          </UserDetails>
          
          <SettingsButton onClick={handleSettings} variant="info">
            <SettingsIcon  />
            {i18n.settings.buttons.settings}
          </SettingsButton>
          
          <LogoutButton onClick={handleLogout} variant="danger">
            <LogoutIcon size={16} color="#fff" />
            {i18n.common.buttons.logOut}
          </LogoutButton>
        </ModalContentWrapper>
      </ModalContainer>
      
      <AddEditUserModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSave={handleUserSave}
        editingUser={editingUser}
        currentUserEmail={user?.email}
        disableRoleAndEmail={true}
      />
    </Overlay>
  );
};

export default UserProfileModal;
